import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { View, Button } from "@aws-amplify/ui-react";
import { MdOutlineClose, MdSearch, MdKeyboardArrowDown } from "react-icons/md";
import { IoSendSharp } from "react-icons/io5";
import { useAuth0 } from "@auth0/auth0-react";
import * as ExcelJS from 'exceljs';

import Querydata from "../../components/Querydata/Querydata";
import PreviewHeader from "../../components/PreviewHeader/PreviewHeader";
import PreviewTable from "../../components/PreviewTable/PreviewTable";
import QaAutoPopulation from "../../components/QaAutoPopulation/QaAutoPopulation";
import { getNlGraphData, uploadFile, readSelectedFile, autoQaFillForm, getProjectInfoApi } from "../../services/AxiosServices";
import "./qanewquery.css";

const QANewquery = () => {
    const { user, getAccessTokenSilently } = useAuth0();
    const { projectName } = useParams();
    const userName = user?.preferred_username || "Moonjelly User";

    const [projectId, setProjectId] = useState("");
    const [projectIdError, setProjectIdError] = useState("");

    const [qaFile, setQAFile] = useState<File | null>(null);
    const [sourceFile, setSourceFile] = useState<File | null>(null);
    const [selectedQAFileName, setSelectedQAFileName] = useState("");
    const [selectedSourceFileName, setSelectedSourceFileName] = useState("");
    const [selectedFiles, setSelectedFiles] = useState<any>([]);

    const [previewData, setPreviewData] = useState<any>(null);
    const [previewSourceData, setPreviewSourceData] = useState<any>(null);

    const [showSearchInput, setShowSearchInput] = useState(false);
    const [showQuerydata, setShowQuerydata] = useState(false);
    const [showPreviewfile, setShowPreviewfile] = useState(false);
    const [showFilePreview, setShowFilePreview] = useState(true);
    const [showQaPreviewFile, setShowQaPreviewFile] = useState(false);
    const [isSourceLoaderEnabled, setIsSourceLoaderEnabled] = useState(false);

    const [isAutoPopulationEnabled, setIsAutoPopulationEnabled] = useState(false);
    const [isProjectLoading, setIsProjectLoading] = useState(false);

    const [sourceSearchQuery, setSourceSearchQuery] = useState("");
    const [sourceSearchFile, setSourceSearchFile] = useState<any | null>(null);

    const [qaFileError, setQaFileError] = useState("");
    const [sourceFileError, setSourceFileError] = useState("");
    const [projectInfo, setProjectInfo] = useState<any>(null);
    const [percentageValue, setPercentageValue] = useState<any>("")
    const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [approvedByFilter, setApprovedByFilter] = useState<string[]>([]);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showApprovedDropdown, setShowApprovedDropdown] = useState(false);
    const statusDropdownRef = useRef<HTMLDivElement | null>(null);
    const approvedDropdownRef = useRef<HTMLDivElement | null>(null);
    const [ownerName, setOwnerName] = useState<any>("");
    const [numberOfQuestions, setNumberOfQuestions] = useState<any>("");

    const clearFilters = () => {
        setSearchText("");
        setStatusFilter([]);
        setApprovedByFilter([]);
    };

    const toggleStatusOption = (option: string) => {
        setStatusFilter((prev) => {
            if (prev.includes(option)) return prev.filter((p) => p !== option);
            return [...prev, option];
        });
    };

    const filteredProjectInfo = useMemo(() => {
        if (!Array.isArray(projectInfo)) return projectInfo;
        const q = (searchText || "").trim().toLowerCase();
        return projectInfo.filter((row: any) => {
            if (q) {
                const question = (row.question || row.Question || "").toString().toLowerCase();
                if (!question.includes(q)) return false;
            }
            if (statusFilter && statusFilter.length > 0) {
                const status = (row.status || row.Status || "").toString().toLowerCase();
                const matches = statusFilter.some((s) => s.toString().toLowerCase() === status);
                if (!matches) return false;
            }
            if (approvedByFilter && approvedByFilter.length > 0) {
                const approver = (row.approver_id.approved_name || "").toString().toLowerCase();
                if (!approver.includes(approvedByFilter[0].toString().toLowerCase())) return false;
            }
            return true;
        });
    }, [projectInfo, searchText, statusFilter, approvedByFilter]);

    const statusActive = statusFilter.length > 0;
    const approvedActive = approvedByFilter.length > 0;

    const approversList = useMemo(() => {
        if (!Array.isArray(projectInfo)) return [];
        const names = projectInfo.map((row: any) => {
            if (row.approver_id.approved_name) return row.approver_id.approved_name;
            return null;
        }).filter(Boolean);
        return Array.from(new Set(names));
    }, [projectInfo]);



    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
                setShowStatusDropdown(false);
            }
            if (approvedDropdownRef.current && !approvedDropdownRef.current.contains(target)) {
                setShowApprovedDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    useEffect(() => {
        const fetchData = async () => {
            if (projectName) {
                setIsAutoPopulationEnabled(true);
                setIsProjectLoading(true);
                await fetchProjectInfo("", projectName);
            }
            else {
                setIsAutoPopulationEnabled(false);
            }
        };
        fetchData();
    }, [projectName]);

    const handleProjectIdChange = (event) => {
        setIsAutoPopulationEnabled(false);
        setProjectId(event.target.value);
        setProjectIdError("");
    };

    const parseExcelFile = async (file: Blob) => {
        try {
            const buffer = await file.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(buffer);
            const worksheet = workbook.getWorksheet(1);
            const json: any[] = [];
            if (worksheet) {
                worksheet.eachRow({ includeEmpty: true }, (row) => {
                    const obj: any = {};
                    row.eachCell({ includeEmpty: true }, (cell, colIndex) => {
                        const col = worksheet.columns[colIndex - 1];
                        if (!col) return;
                        obj[col.header as string] = cell.value;
                    });
                    json.push(obj);
                });
            }
            setPreviewSourceData(json);
            setShowPreviewfile(true);
            setIsSourceLoaderEnabled(false);
        } catch (e) {
            console.error("Error parsing file:", e);
        }
    };

    const handleChooseFile = async (fileName: string) => {
        try {
            const token = await getAccessTokenSilently();
            const response = await readSelectedFile(fileName, token);
            parseExcelFile(response);
            setSelectedSourceFileName(fileName);
            if (sourceFileError) setSourceFileError("");
        } catch (error) {
            console.error("Error reading file:", error);
        }
    };

    const handleFileSelection = async (event) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles?.length) return;

        const file = selectedFiles[0];
        setSourceFile(file);
        setIsSourceLoaderEnabled(true);
        setSourceFileError("");
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("owner", userName);
            formData.append("title", file.name);
            const token = await getAccessTokenSilently();
            await uploadFile(formData, token);
        } catch (error) {
            console.error("Error uploading file:", error);
        }
        //handleChooseFile(file.name);
        setSelectedSourceFileName(file.name);
        setPreviewSourceData(null);
        setIsSourceLoaderEnabled(false);
        setShowQuerydata(false);
        setShowFilePreview(false);
        setShowPreviewfile(true);
        event.target.value = "";
    };

    const handleSearchSubmit = async () => {
        const query = sourceSearchQuery;
        if (!query.trim()) return;

        try {
            setIsSourceLoaderEnabled(true);
            const token = await getAccessTokenSilently();
            const data = await getNlGraphData(query, token);
            setSourceSearchFile(data);
            setIsSourceLoaderEnabled(false);

        } catch (error) {
            console.error("Error fetching data:", error);
        }

        setShowSearchInput(false);
        setShowQuerydata(true);
        setShowFilePreview(false);
    };

    const handleConfirm = async () => {
        setProjectIdError("");
        setSourceFileError("");
        setQaFileError("");
        const token = await getAccessTokenSilently();
        if (!projectId.trim()) {
            setProjectIdError("Project ID is required");
        }
        if (!qaFile) {
            setQaFileError("Please select a valid Q&A file");
        }
        if (selectedFiles.length === 0 && !sourceFile) {
            setSourceFileError("Please select a valid Source file");
        }

        if (!projectId.trim() || (!qaFile) || (selectedFiles.length === 0 && !sourceFile)) {
            return;
        }
        const formData = new FormData();
        formData.append("context_filenames", JSON.stringify(selectedFiles));
        formData.append("project_name", projectId);
        if (qaFile) formData.append("qa_file", qaFile);

        try {
            const res = await autoQaFillForm(formData, token);
            if (res) {
                setIsProjectLoading(true);
                fetchProjectInfo();
            }
        } catch (error) {
            console.error("Error uploading file:", error);
        }

    };

    // Replace the existing useEffect and fetchProjectInfo function with these fixed versions:

    const fetchProjectInfo = async (prevSelectedRowData: any = null, projectName: string | null = null) => {
        try {
            
            const token = await getAccessTokenSilently();
            const response = await getProjectInfoApi(projectName ? projectName : projectId, token);
            if (response && typeof response === 'object' && 'result' in response && 'progress_percentage' in response && 'owner_name' in response && 'total_questions' in response) {
                const progress = response.progress_percentage;
                setOwnerName(response.owner_name);
                setNumberOfQuestions(response.total_questions);
                setPercentageValue(progress || 0);
                setProjectInfo(response.result);
                // Enable auto-population only when the API returned result items
                const hasResults = Array.isArray(response.result) && response.result.length > 0;
                setIsAutoPopulationEnabled(!!hasResults);
                if (prevSelectedRowData && Array.isArray(response.result)) {
                    const updatedRow = response.result.find((row) => row.id === prevSelectedRowData.id);
                    if (updatedRow) {
                        setSelectedRowData((prev) => {
                            if (prev && prev.id === updatedRow.id) {
                                return { ...prev, ...updatedRow };
                            }
                            return prev;
                        });
                    }
                }
                // if there are results or result is empty array, mark loading finished
                setIsProjectLoading(false);
            } else {
                setIsProjectLoading(false);
                console.error("Unexpected response format:", response);
            }
        } catch (error) {
            console.error("Error resetting state:", error);
            setIsProjectLoading(false);
        }
    };

    // Only start polling when projectInfo has a value (truthy / non-empty array).
    const shouldPoll = useMemo(() => {
        if (!projectInfo) return false;
        if (Array.isArray(projectInfo)) return projectInfo.length > 0;
        return true;
    }, [projectInfo]);

    useEffect(() => {
        if (!shouldPoll) return;

        const interval = setInterval(() => {
            if (projectName || projectId) {
                fetchProjectInfo(selectedRowData, projectName);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [shouldPoll, selectedRowData, projectName, projectId]);


    const handleQaFileSelection = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const buffer = await file.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(buffer);
            const worksheet = workbook.getWorksheet(1);
            const jsonData: any[] = [];
            if (worksheet) {
                worksheet.eachRow({ includeEmpty: true }, (row) => {
                    const obj: any = {};
                    row.eachCell({ includeEmpty: true }, (cell, colIndex) => {
                        const col = worksheet.columns[colIndex - 1];
                        if (!col) return;
                        obj[col.header as string] = cell.value;
                    });
                    jsonData.push(obj);
                });
            }

            setPreviewData(jsonData);
            setSelectedQAFileName(file.name);
            setQAFile(file);
            setQaFileError("");
            setShowQaPreviewFile(true);
        } catch (error) {
            console.error("Error reading file:", error);
        }
    };
    return (
        <View>
            <div className="query-section qa-new-query">
                {isAutoPopulationEnabled && (projectId || projectName !== undefined) ?
                    <>
                        {(
                            (!isProjectLoading )
                                ? (
                                    <>
                                        <div className="left-panel">
                                            <div className="progress-ring-container">
                                                <div className="ring">
                                                    <div
                                                        className="ring-progress"
                                                        style={{
                                                            background: `conic-gradient(#1976d2 ${percentageValue}%, transparent 0deg)`,
                                                        }}
                                                    >
                                                        <div className="ring-center">
                                                            {percentageValue}%
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="ring-label">{projectId ? projectId.toUpperCase() : projectName?.toUpperCase()}
                                                    {ownerName && (
                                                        <label className="file-label">Owner: {ownerName}</label>
                                                    )}
                                                    {numberOfQuestions && (
                                                        <label className="file-label">{numberOfQuestions} questions</label>
                                                    )}
                                                </div>

                                            </div>
                                            <div className="filter-bar">
                                                {((searchText || '').trim().length > 0 || statusActive || approvedActive) && (
                                                    <button className="clear-filters" onClick={clearFilters}>clear filters</button>
                                                )}
                                                <div className="question-search-input">
                                                    <MdSearch size={20} />
                                                    <input
                                                        type="text"
                                                        placeholder="search question"
                                                        value={searchText}
                                                        onChange={(e) => setSearchText(e.target.value)}
                                                    />
                                                </div>
                                                <div className="dropdown-filter" ref={statusDropdownRef} tabIndex={0}>
                                                    <button className={`dropdown-toggle ${statusActive ? 'filters-active' : ''}`} onClick={() => setShowStatusDropdown((s) => !s)}>
                                                        <span>Status</span>
                                                        {statusFilter.length > 0 ? <span className="count-badge">{statusFilter.length}</span> : <MdKeyboardArrowDown size={20} />}
                                                    </button>
                                                    {showStatusDropdown && (
                                                        <div className="dropdown-menu">
                                                            <label className="dropdown-item">
                                                                <input type="checkbox" checked={statusFilter.includes('approved')} onChange={() => toggleStatusOption('approved')} />
                                                                <span className="custom-checkbox" aria-hidden></span>
                                                                <span className="dropdown-label">Approved</span>
                                                            </label>
                                                            <label className="dropdown-item">
                                                                <input type="checkbox" checked={statusFilter.includes('under_review')} onChange={() => toggleStatusOption('under_review')} />
                                                                <span className="custom-checkbox" aria-hidden></span>
                                                                <span className="dropdown-label">Under Review</span>
                                                            </label>
                                                            <label className="dropdown-item">
                                                                <input type="checkbox" checked={statusFilter.includes('not_started')} onChange={() => toggleStatusOption('not_started')} />
                                                                <span className="custom-checkbox" aria-hidden></span>
                                                                <span className="dropdown-label">Not Started</span>
                                                            </label>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="dropdown-filter" ref={approvedDropdownRef} tabIndex={0}>
                                                    <button className={`dropdown-toggle ${approvedActive ? 'filters-active' : ''}`} onClick={() => setShowApprovedDropdown((s) => !s)}>
                                                        <span>{approvedByFilter.length > 0 ? approvedByFilter[0] : 'Approved by'}</span>
                                                        <MdKeyboardArrowDown size={20} />
                                                    </button>
                                                    {showApprovedDropdown && (
                                                        <div className="dropdown-menu">
                                                            {approversList.length > 0 && (
                                                                approversList.map((name) => (
                                                                    <div key={name} className="dropdown-item" onClick={() => { setApprovedByFilter([name]); setShowApprovedDropdown(false); }}>
                                                                        {name}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <PreviewTable
                                                previewData={filteredProjectInfo}
                                                isQAEnabled={true}
                                                onRowClick={(rowData) => setSelectedRowData(rowData)}
                                                selectedRowData={selectedRowData}
                                                setSelectedRowData={setSelectedRowData}
                                            />
                                        </div>
                                        <div className="right-panel">
                                            {selectedRowData &&
                                                <QaAutoPopulation
                                                    fetchProjectInfo={() => fetchProjectInfo(selectedRowData, projectName)}
                                                    header={selectedRowData?.question}
                                                    status={selectedRowData?.status}
                                                    options={
                                                        Array.isArray(selectedRowData?.answer)
                                                            ? selectedRowData.answer.map((ans) => ({ ...selectedRowData, answer: ans }))
                                                            : [
                                                                {
                                                                    ...selectedRowData, answer: selectedRowData?.answer ? selectedRowData?.answer : ""
                                                                }
                                                            ]
                                                    }
                                                />
                                            }

                                        </div>
                                    </>
                                ) : (
                                    <div className="left-panel-empty">
                                        {isProjectLoading ? (
                                            <div style={{ textAlign: 'center', padding: '24px' }}>
                                                <div className="loader-spinner"></div>
                                                <p>Loading project info. Please wait...</p>
                                            </div>
                                        ) : null}
                                    </div>
                                )
                        )}


                    </>
                    :
                    <>
                        <div className="left-panel">
                            <div className="top-section">
                                <h4 className="section-label-top">PROJECT ID</h4>
                                {projectIdError && (
                                    <div className="error-message">
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            style={{ marginRight: "8px", flexShrink: 0 }}
                                        >
                                            <path d="M12 2L22 20H2L12 2Z" fill="#dc3545" />
                                            <path
                                                d="M12 8V13"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                            />
                                            <circle cx="12" cy="17" r="1" fill="white" />
                                        </svg>
                                        {projectIdError}
                                    </div>
                                )}
                                <div className="file-search-container">
                                    <div className="search-input-wrapper">
                                        <input
                                            type="text"
                                            placeholder="eg. Anonymous_05_2024"
                                            className="search-input"
                                            value={projectId}
                                            onChange={handleProjectIdChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <h4 className="section-label-top">SOURCE</h4>
                            {/* Source Section */}
                            {!showSearchInput && !showQuerydata && !showPreviewfile ? (
                                <>
                                    <div className="file-controls">
                                        <Button className="custom-btn" onClick={() => setShowSearchInput(true)}>
                                            Search file
                                        </Button>
                                        <Button
                                            className="custom-btn"
                                            onClick={() => document.getElementById("source-file-input")?.click()}
                                        >
                                            Upload file
                                        </Button>
                                    </div>
                                    {sourceFileError && (
                                        <div className="error-message">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                                                <path d="M12 2L22 20H2L12 2Z" fill="#dc3545" />
                                                <path d="M12 8V13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                                <circle cx="12" cy="17" r="1" fill="white" />
                                            </svg>
                                            {sourceFileError}
                                        </div>
                                    )}
                                </>
                            ) : showSearchInput ? (
                                <div className="file-search-container">
                                    <div className="search-input-wrapper">
                                        <input
                                            type="text"
                                            value={sourceSearchQuery}
                                            onChange={(e) => setSourceSearchQuery(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
                                            placeholder="ex. Covid data spreadsheet"
                                            className="search-input"
                                            autoFocus
                                        />
                                        <button onClick={() => { setShowSearchInput(false); setSourceSearchQuery(""); setSourceFileError(""); }} className="search-close-btn">
                                            <MdOutlineClose size={30} />
                                        </button>
                                        <button
                                            onClick={() => { handleSearchSubmit(); setSourceFileError("") }}
                                            className="search-submit-btn"
                                            disabled={!sourceSearchQuery.trim()}
                                        >
                                            <IoSendSharp size={40} />
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {showQuerydata && (
                                <div className="querydata-wrapper">
                                    <Querydata
                                        onChooseFile={(f) => handleChooseFile(f)}
                                        onClear={() => {
                                            setShowQuerydata(false);
                                            setSourceSearchQuery("");
                                            setSourceFileError("");
                                            setShowFilePreview(true);
                                            setSelectedFiles([]);
                                        }}
                                        onChooseFiles={(files) => {
                                            setSelectedFiles(files);
                                            setSourceFileError("");
                                        }}
                                        nlGraphData={sourceSearchFile}
                                        searchQuery={sourceSearchQuery}
                                        fileError={sourceFileError}
                                        isRadio={false}

                                    />
                                </div>
                            )}

                            {showPreviewfile && (
                                <div className="previewfile-wrapper">
                                    <PreviewHeader selectedFileName={selectedSourceFileName} previewData={previewSourceData} onClear={() => { setShowPreviewfile(false); setSourceFile(null); setShowFilePreview(true); setSourceFileError(""); }}
                                        isSourceFile={true}
                                    />
                                </div>
                            )}

                            {showFilePreview && (
                                <div className="file-preview">
                                    <div className="file-preview-inner">
                                        {isSourceLoaderEnabled ? (
                                            <>
                                                <div className="loader-spinner"></div>
                                                <p>Please wait while we process your request.</p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="preview-text">File List</p>
                                                <p className="preview-subtext">
                                                    Pick your files, and they will appear here.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Hidden input */}
                            <input
                                id="source-file-input"
                                type="file"
                                style={{ display: "none" }}
                                onChange={(e) => handleFileSelection(e)}
                                accept=".xls,.xlsx,.txt,.pdf,.csv,.doc,.docx"
                            />

                            {/* Q&A Section */}
                            <div className="qa-section">
                                <h4 className="section-label-top">Q&A File</h4>
                                {!showQaPreviewFile ? (
                                    <>
                                        <div className="file-controls">
                                            <Button className="custom-btn" onClick={() => document.getElementById("qa-file-input")?.click()}> Upload file </Button>
                                        </div>
                                        {qaFileError && (
                                            <div className="error-message">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                                                    <path d="M12 2L22 20H2L12 2Z" fill="#dc3545" />
                                                    <path d="M12 8V13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                                    <circle cx="12" cy="17" r="1" fill="white" />
                                                </svg>
                                                {qaFileError}
                                            </div>
                                        )}
                                        <input
                                            id="qa-file-input"
                                            type="file"
                                            accept=".xls,.xlsx"
                                            style={{ display: "none" }}
                                            onChange={(e) => handleQaFileSelection(e)}
                                        />
                                    </>
                                ) : null}
                            </div>

                            {showQaPreviewFile && previewData && (
                                <div className="previewfile-wrapper">
                                    <PreviewHeader selectedFileName={selectedQAFileName} previewData={previewData} onClear={() => { setShowQaPreviewFile(false); setQaFileError(""); setQAFile(null); }} isSourceFile={false} />
                                </div>
                            )}

                            {/* Confirm */}
                            <div className="center">
                                <button className="generate-btn" onClick={handleConfirm}>
                                    Confirm
                                </button>
                            </div>
                        </div>

                        {/* Right Panel */}
                        <div className="right-panel">
                            <div className="result-placeholder">
                                <h4 className="section-label-top">Q&A LIST</h4>
                                {showQaPreviewFile && previewData ? (
                                    <PreviewTable previewData={previewData} isQAEnabled={false} />
                                ) : (
                                    <div className="no-data-container">
                                        <div className="no-data-message">
                                            <img
                                                src={`${process.env.PUBLIC_URL}/nothing_to_show yet.jpg`}
                                                alt="No history"
                                                className="no-data-image"
                                            />
                                            <h3>Nothing to show yet!</h3>
                                            <p>Select a Q&A File and the list of questions will pop up here.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                }
            </div>
        </View>
    );
};

export default QANewquery;